import { t, type TFunction } from 'i18next';
import {
  CircleDollarSignIcon,
  CircleHelpIcon,
  CirclePauseIcon,
  CirclePlayIcon,
  CreditCardIcon,
  CrosshairIcon,
  FileTextIcon,
  FlagIcon,
  GalleryHorizontalIcon,
  ImageIcon,
  LinkIcon,
  ListChecksIcon,
  LocateFixedIcon,
  LogInIcon,
  MailIcon,
  MonitorSmartphoneIcon,
  NetworkIcon,
  NotepadTextIcon,
  PanelTopIcon,
  RefreshCcwIcon,
  RotateCcwIcon,
  ShieldIcon,
  SquareCheckBigIcon,
  SquareIcon,
  TagIcon,
  TimerIcon,
  TypeIcon,
  UserRoundIcon,
  VariableIcon,
  WaypointsIcon,
  WebhookIcon,
  WorkflowIcon,
  WrenchIcon,
} from 'lucide-react';
import { Accept } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import { AIResponseIcon } from '@/components/atoms/Icons/AIResponseIcon';
import { CircleChevronsRightIcon } from '@/components/atoms/Icons/CircleChevronsRightIcon';
import FilePreview from '@/components/atoms/Icons/FilePreview';
import { MessageQuestionIcon } from '@/components/atoms/Icons/MessageQuestionIcon';
import SurveyPreview from '@/components/atoms/Icons/SurveyPreview';
import URLPreview from '@/components/atoms/Icons/URLPreview';
import WebviewPreview from '@/components/atoms/Icons/WebviewPreview';
import { REMINDER_CONSTRAINTS } from '@/components/atoms/ReminderTimeSelect/ReminderTimeSelect';
import { Features } from '@/shared/types/account';
import {
  Action,
  CarouselCard,
  Question,
  QuestionOption,
  ReminderAction,
} from '@/shared/types/action';
import { Condition, Node } from '@/shared/types/node';
import { ENHANCER_ACCOUNT_IDS, ENHANCER_LANGUAGES, LAST_ACTIONS } from '@/util/constants';
import { formatSecondsToReadable } from '@/util/dateUtils';
import { getCarouselPlaceholder, getWebviewsUrl } from '@/util/util';

interface IconByTypeProps {
  id: string;
  type?: string;
  size?: number;
  color?: string;
  stepsMode?: boolean;
}
export const TYPES = {
  NODE: 'node',
  ACTION: 'action',
  CONDITION: 'condition',
  QUESTION: 'question',
  NOREQUISITE: 'norequisite',
  CONDITION_BOX: 'condition-box',
  OPTION: 'option',
} as const;

export const NO_REQUISITE_INDEX = -2;

export const iconByType = ({ id, size, color, type, stepsMode = false }: IconByTypeProps) => {
  switch (id) {
    case 'intent':
      return <LocateFixedIcon size={size} color={color} />;
    case 'unknown':
    case 'unknown_root':
      return <CircleHelpIcon size={size} color={color} />;
    case 'condition':
      return <NetworkIcon size={size} color={color} />;
    case 'text':
      return <TypeIcon size={size} color={color} />;
    case 'handover':
      return <UserRoundIcon size={size} color={color} />;
    case 'switch_channel':
      return <MonitorSmartphoneIcon size={size} color={color} />;
    case 'survey':
      return <NotepadTextIcon size={size} color={color} />;
    case 'reset':
      return stepsMode ? (
        <CircleDollarSignIcon size={size} color={color} />
      ) : (
        <RefreshCcwIcon size={size} color={color} />
      );
    case 'pause':
      return <CirclePauseIcon size={size} color={color} />;
    case 'event':
      return type === TYPES.ACTION ? (
        <CircleChevronsRightIcon size={size} color={color} />
      ) : (
        <FlagIcon size={size} color={color} />
      );
    case 'jump_to':
      return <CircleChevronsRightIcon size={size} color={color} />;
    case 'starting_node':
      return <LogInIcon size={size} color={color} />;
    case 'webhook':
      return <WebhookIcon size={size} color={color} />;
    case 'video':
      return <CirclePlayIcon size={size} color={color} />;
    case 'image':
      return <ImageIcon size={size} color={color} />;
    case 'file':
      return <FileTextIcon size={size} color={color} />;
    case 'url':
      return <LinkIcon size={size} color={color} />;
    case 'close':
      return <SquareCheckBigIcon size={size} color={color} />;
    case 'replay':
      return <RotateCcwIcon size={size} color={color} />;
    case 'webview':
      return <PanelTopIcon size={size} color={color} />;
    case 'carousel':
      return <GalleryHorizontalIcon size={size} color={color} />;
    case 'tag':
      return <TagIcon size={size} color={color} />;
    case 'email':
      return <MailIcon size={size} color={color} />;
    case 'googlesheet':
      return <FileTextIcon size={size} color={color} />;
    case 'any':
      return <SquareIcon size={size} color={color} />;
    case 'question':
      return <MessageQuestionIcon size={size} color={color} />;
    case 'set_variables':
      return stepsMode ? (
        <CircleDollarSignIcon size={size} color={color} />
      ) : (
        <VariableIcon size={size} color={color} />
      );
    case 'payment':
      return <CreditCardIcon size={size} color={color} />;
    case 'billing':
      return <CreditCardIcon size={size} color={color} />;
    case 'entity':
      return <CrosshairIcon size={size} color={color} />;
    case 'reminder':
      return <TimerIcon size={size} color={color} />;
    case 'whatsapp_flow':
      return <WorkflowIcon size={size} color={color} />;
    case 'question_set':
      return <ListChecksIcon size={size} color={color} />;
    case 'tool_invocation':
    case 'mcp_tool':
      return <WrenchIcon size={size} color={color} />;
    case 'auth':
    case 'sub_agent':
    case 'sub_agent_auth':
      return <ShieldIcon size={size} color={color} />;
    case 'ai_response':
    case 'sub_agent_ai_response':
      return <AIResponseIcon size={size} color={color} />;
    default:
      return <WaypointsIcon size={size} color={color} />;
  }
};

export const previewIconByType = (id: string) => {
  switch (id) {
    case 'file':
      return <FilePreview />;
    case 'webview':
      return <WebviewPreview />;
    case 'survey':
      return <SurveyPreview />;
    case 'url':
      return <URLPreview />;
  }
};

export const randId = (length = 4) => {
  return Math.random()
    .toString(36)
    .substring(2, 2 + length);
};

/** Generates a random string guaranteed to start with a letter (a-z). */
export const generateVarName = (length = 4) => {
  const firstChar = Math.floor(Math.random() * 26 + 10).toString(36);
  return firstChar + randId(length - 1);
};

export const SYS_CONTEXT_VARIABLES = [
  '$sys-channel',
  '$sys-unknown_counter',
  '$sys-user_message_counter',
  '$sys-desk',
  '$sys-business',
  '$tags',
];

export const USER_INFO_VARIABLES = ['$user.verified', '$user.external_id'];

export const LEAD_DATA_VARIABLES = [
  '$user.display_name',
  '$user.email',
  '$user.phone',
  '$user.address',
  '$user.language',
];

export const VARIABLE_LABELS = {
  '$user.display_name': 'Name',
  '$user.email': 'Email',
  '$user.phone': 'Phone',
  '$user.address': 'Address',
  '$user.language': 'Language',
  '$user.verified': 'Verified',
  '$user.external_id': 'External ID',
  '$sys-channel': 'Channel',
  '$sys-unknown_counter': 'Unknown counter',
  '$sys-user_message_counter': 'User message counter',
  '$sys-desk': 'Environment',
  '$sys-business': 'Business hours',
} as const;

export const REVERSE_VARIABLE_LABELS = {
  Name: '$user.display_name',
  Email: '$user.email',
  Phone: '$user.phone',
  Address: '$user.address',
  Language: '$user.language',
  Verified: '$user.verified',
  'External ID': '$user.external_id',
  Channel: '$sys-channel',
  'Unknown counter': '$sys-unknown_counter',
  'User message counter': '$sys-user_message_counter',
  Environment: '$sys-desk',
  'Business hours': '$sys-business',
};

export const EXAMPLE_VARIABLES_VALUE = {
  '$user.display_name': 'Test name',
  '$user.email': 'test@email.com',
  '$user.phone': '1234567890',
  '$user.address': '123 Test St.',
  '$user.language': 'en',
  '$user.verified': 'true',
  '$user.external_id': '1234567890',
  '$sys-unknown_counter': '1',
  '$sys-user_message_counter': '2',
} as const;

export const getVariableLabel = (variable: string) => {
  if (VARIABLE_LABELS[variable]) {
    return VARIABLE_LABELS[variable];
  }

  return variable;
};

export const generateDefaultCarouselCard = (id: number): CarouselCard => ({
  title: t('dialog.carousel.example_title', { 0: id }),
  subtitle: t('dialog.carousel.example_subtitle', { 0: id }),
  media: { type: 'image', url: getCarouselPlaceholder({ id }) },
  buttons: [
    {
      type: 'postback',
      value: t('actions.generate_name', { 0: randId() }),
      label: `${t('dialog.carousel.button')} 1`,
      id: randId(6),
    },
  ],
});

const nextFreeIndex = (isTaken: (index: number) => boolean) => {
  let index = 1;
  while (isTaken(index)) {
    index += 1;
  }
  return index;
};

const defaultOption = (index: number): QuestionOption => ({
  label: t('dialog.question_set.default_option', { 0: index }),
  value: `option_${index}`,
});

/** A default option whose label and value are both unused by `taken`. */
export const newQuestionOption = (taken: QuestionOption[] = []): QuestionOption =>
  defaultOption(
    nextFreeIndex((index) => {
      const { label, value } = defaultOption(index);
      return taken.some((option) => option.label?.trim() === label || option.value === value);
    })
  );

export const defaultQuestionOptions = (): QuestionOption[] => {
  const first = newQuestionOption();
  return [first, newQuestionOption([first])];
};

/** A default question whose key is not among `takenKeys`. */
export const newQuestion = (takenKeys: string[] = []): Question => ({
  key: `question_${nextFreeIndex((index) => takenKeys.includes(`question_${index}`))}`,
  header: t('dialog.question_set.default_header'),
  question: t('dialog.question_set.default_question'),
  type: 'single',
  allow_other: true,
  options: defaultQuestionOptions(),
});

export const DEFAULT_HEIGHT = 'tall';
export const DEFAULT_RANDOM_URL = 'https://picsum.photos/300';

export const defaultPropsByType = (id: string, randomValue?: string) => {
  const name = t('actions.generate_name', { 0: randId() });
  const text = t('actions.generate_text', { 0: randId() });
  switch (id) {
    // Triggers
    case 'intent':
      return { name, requisites: [], actions: [], intent: randomValue };
    case 'event':
      return { name, requisites: [], actions: [], trigger_node_id: randomValue };
    case 'unknown':
      return { name, actions: [] };
    case 'sub_agent_auth':
      // success/failure node ids start empty — the builder must point them at
      // real nodes. Defaulting them to random uuids (the original auth-node
      // behavior) produced targets referencing nodes that don't exist while
      // still passing the "required" validation.
      return {
        type: 'sub_agent',
        sub_agent_config: { type: 'auth', trigger: { when: 'conversation_starts' as const } },
      };
    case 'sub_agent_ai_response':
      return {
        type: 'sub_agent',
        sub_agent_config: { type: 'ai_response' as const, guidelines: '' },
      };

    // Actions
    case 'condition':
      return {
        match: 'any',
        rules: [],
        actions: [],
        condition_id: uuidv4(),
        name: t('actions.generate_condition', { 0: randId() }),
      };
    case 'text':
      return { texts: [text] };
    case 'video':
      return {
        type: 'video',
        name,
        url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4',
        size: 2299653,
      };
    case 'image':
      return { name, url: DEFAULT_RANDOM_URL };
    case 'url':
      return { url: DEFAULT_RANDOM_URL };
    case 'file':
      return {
        type: 'file',
        name,
        url: 'https://www.claymath.org/sites/default/files/official_problem_description.pdf',
        size: 10000000,
      };
    case 'pause':
      return { duration: 3, show_typing: true };
    case 'carousel':
      return { activeCard: 0, cards: [generateDefaultCarouselCard(1)] };
    case 'survey':
      return {
        name: t('actions.types.survey'),
        label: t('dialog.survey_label'),
        url: `${getWebviewsUrl()}/common/survey`,
        height: DEFAULT_HEIGHT,
      };
    case 'reset':
      return { name };
    case 'handover':
      return { name, external: false };
    case 'switch_channel':
      return { channel: 'web' };
    case 'tag':
      return { tags: [t('dialog.pre-fill.tag')] };
    case 'webview': {
      return {
        name,
        label: t('dialog.carousel.button'),
        url: DEFAULT_RANDOM_URL,
        height: DEFAULT_HEIGHT,
        trigger_node_id: randomValue,
      };
    }
    case 'email':
      return {
        from: 'no-reply@moveo.ai',
        to: ['test@mail.com'],
        subject: t('dialog.pre-fill.subject'),
        body: text,
      };
    case 'googlesheet':
      return {
        sheet_id: t('dialog.pre-fill.sheet_id'),
        spreadsheet_id: t('dialog.pre-fill.spreadsheet_id'),
        inputs: [t('dialog.pre-fill.input')],
      };
    case 'webhook':
      return { webhook_id: randomValue, fallback: [{ type: 'text', texts: [text] }] };
    case 'set_variables':
      return {
        variables: [
          { variable: t('dialog.pre-fill.set_variable'), value: t('dialog.pre-fill.value') },
        ],
      };
    case 'reminder':
      return { reminder_seconds: REMINDER_CONSTRAINTS.DEFAULT_SECONDS };
    case 'whatsapp_flow':
      return {
        whatsapp_flow: { flow_id: '', flow_body: '', flow_cta: '', flow_action: 'navigate' },
      };
    case 'question_set':
      return {
        question_set: { title: t('dialog.question_set.default_title'), questions: [newQuestion()] },
      };
    default:
      return {};
  }
};

export type ItemsType = { title: string; id: string; type: 'node' | 'condition' | 'action' };

// new toolkit
export const START_WITH = [
  { title: 'actions.types.intent', id: 'intent', type: TYPES.NODE },
  { title: 'actions.types.event', id: 'event', type: TYPES.NODE },
  { title: 'actions.types.unknown', id: 'unknown', type: TYPES.NODE },
];

export const SUBAGENTS = [
  { title: 'actions.types.authentication', id: 'sub_agent_auth', type: TYPES.ACTION },
];
export const RESPONSES = [
  { title: 'actions.types.text', id: 'text', type: TYPES.ACTION },
  // Single-turn AI-generated response (Synthesis). Backed by the sub_agent
  // architecture but presented as a response kind, not a multi-turn subagent.
  { title: 'actions.types.ai_response', id: 'sub_agent_ai_response', type: TYPES.ACTION },
  { title: 'actions.types.image', id: 'image', type: TYPES.ACTION },
  { title: 'actions.types.carousel', id: 'carousel', type: TYPES.ACTION },
  { title: 'actions.types.webview', id: 'webview', type: TYPES.ACTION },
  { title: 'actions.types.video', id: 'video', type: TYPES.ACTION },
  { title: 'actions.types.file', id: 'file', type: TYPES.ACTION },
  { title: 'actions.types.survey', id: 'survey', type: TYPES.ACTION },
  { title: 'actions.types.url', id: 'url', type: TYPES.ACTION },
  { title: 'actions.types.whatsapp_flow', id: 'whatsapp_flow', type: TYPES.ACTION },
  { title: 'actions.types.question_set', id: 'question_set', type: TYPES.ACTION },
];
export const OPERATIONS = [
  { title: 'actions.types.question', id: 'question', type: TYPES.ACTION },
  { title: 'actions.types.jump_to', id: 'event', type: TYPES.ACTION },
  { title: 'actions.types.condition', id: 'condition', type: TYPES.CONDITION },
  { title: 'actions.types.handover', id: 'handover', type: TYPES.ACTION },
  { title: 'actions.types.switch_channel', id: 'switch_channel', type: TYPES.ACTION },
  { title: 'actions.types.tag', id: 'tag', type: TYPES.ACTION },
  { title: 'actions.types.pause', id: 'pause', type: TYPES.ACTION },
  { title: 'actions.types.set_variables', id: 'set_variables', type: TYPES.ACTION },
  { title: 'actions.types.reset_variables', id: 'reset', type: TYPES.ACTION },
  { title: 'actions.types.resolve', id: 'close', type: TYPES.ACTION },
  { title: 'actions.types.replay', id: 'replay', type: TYPES.ACTION },
  { title: 'actions.types.reminder', id: 'reminder', type: TYPES.ACTION },
];
export const EXTENSIONS = [
  { title: 'actions.types.webhook', id: 'webhook', type: TYPES.ACTION },
  { title: 'actions.types.googlesheet', id: 'googlesheet', type: TYPES.ACTION },
  { title: 'actions.types.email', id: 'email', type: TYPES.ACTION },
];

export const generateOption = () => ({
  text: t('dialog.example_option_text', { 0: randId() }),
  label: t('dialog.example_option_label', { 0: randId() }),
  option_id: uuidv4(),
});

export const generateRequisite = () => ({
  save_as: generateVarName(),
  requisite_id: uuidv4(),
  actions: [{ type: 'text', texts: [t('actions.generate_text', { 0: randId() })] }],
});

export const generateRule = () => ({
  name: `$${generateVarName()}`,
  operator: 'not_exist',
  rule_id: uuidv4(),
});

export const generateGuidelineRule = () => ({
  name: `$${randId()}`,
  operator: 'guideline',
  value: t('dialog.condition.guideline_example'),
  rule_id: uuidv4(),
});

export const generateConditionTextAction = () =>
  ({
    texts: [t('actions.generate_text', { 0: randId() })],
    type: 'text',
    action_id: uuidv4(),
    node_id: uuidv4(),
  }) as const;

// Accepted file formats for the dropzone of the File action.
export const ACCEPTED_FILE_FORMATS: Accept = {
  // Images
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  ' image/gif': ['.gif'],
  // Documents
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  // Spreadsheets
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  // Presentations
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  // Archives
  'application/zip': ['.zip'],
  'application/x-rar-compressed': ['.rar'],
  // Text
  'text/plain': ['.txt', '.text'],
  // Audio
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/x-ms-wma': ['.wma'],
  // Video
  'video/mp4': ['.mp4'],
};

/**
 * Returns an empty option object with a unique identifier.
 * @returns {Object} An object representing an empty option.
 * @property {string} text - The text of the option.
 * @property {string} label - The label of the option.
 * @property {string} option_id - The unique identifier of the option.
 */
export const getEmptyOption = () => ({ text: '', label: '', option_id: uuidv4() });

// Actions that should be placed at the end of the flow
export const isFinalAction = (type: string) => {
  return LAST_ACTIONS.includes(type);
};

/**
 * Get a random element from an array.
 *
 * @template T
 * @param {T[]} arr - The input array.
 * @returns {T | null} - A random element from the array, or `null` if the array is empty.
 */
export function getRandomElement<T>(arr: T[]): T | null {
  if (!arr || arr.length === 0) {
    return null;
  }

  const randomIndex = Math.floor(Math.random() * arr.length);
  return arr[randomIndex];
}

/**
 * Checks if a language supports the enhance feature.
 * First checks LaunchDarkly feature flag (nlp_enhancer_languages).
 * Falls back to environment variables (ENHANCER_LANGUAGES and ENHANCER_ACCOUNT_IDS) if feature flag is not available.
 *
 * @param {string} lang - The language code to check
 * @param {string} accountId - The account ID to check
 * @param {Features} [features] - Optional LaunchDarkly feature flags
 * @returns {boolean} - True if the language supports enhance, false otherwise
 */
export const languageSupportsEnhance = (lang: string, accountId: string, features?: Features) => {
  // Priority 1: Check LaunchDarkly feature flag
  // Only return true if language is in the LD list; otherwise fall through to env vars
  if (
    features?.nlp_enhancer_languages &&
    Array.isArray(features.nlp_enhancer_languages) &&
    features.nlp_enhancer_languages.includes(lang)
  ) {
    return true;
  }

  // Priority 2: Fall back to environment variables for backward compatibility
  // Check if account is in ENHANCER_ACCOUNT_IDS (enables ALL languages for this account)
  if (ENHANCER_ACCOUNT_IDS.includes(accountId)) {
    return true;
  }

  // Check if language is in ENHANCER_LANGUAGES
  if (ENHANCER_LANGUAGES.includes(lang)) {
    return true;
  }

  return false;
};

const isReminderAction = (a: Action): a is ReminderAction => a.type === 'reminder';

const collectActionsFromNode = (node: Node): Action[] => {
  const fromConditions = (node.conditions ?? []).flatMap((c) => c.actions ?? []);
  const fromRequisites = (node.requisites ?? []).flatMap((r) => r.actions ?? []);
  return [...(node.actions ?? []), ...fromConditions, ...fromRequisites];
};

/**
 * Build a fresh reminder/timer node payload to drop under `parentId`. Caller is
 * responsible for dispatching `addNode` and re-parenting existing children of
 * `parentId` under the returned node (use {@link collectChildrenForReparenting}).
 */
export const buildReminderNode = (
  parentId: string,
  reminderSeconds: number,
  t: (key: string, options?: Record<string, unknown>) => string
): Node => ({
  name: `${t('common.after')} ${formatSecondsToReadable(reminderSeconds)}`,
  type: 'event',
  node_id: uuidv4(),
  parent_id: parentId,
  requisites: [],
  actions: [],
  is_reminder: true,
});

/** Existing children of `parentId` that the create-new flow needs to re-parent under the new timer. */
export const collectChildrenForReparenting = (nodes: Node[], parentId: string): Node[] =>
  nodes.filter((n) => n.parent_id === parentId);

/**
 * Format a branch's display label using its name (when set) or a localized
 * "Branch N" fallback derived from its index.
 */
export const getBranchLabel = (
  conditionName: string | null,
  conditionIndex: number,
  t: TFunction
): string =>
  conditionName ??
  t('ai_agents.reminder.branch_fallback', 'Branch {{0}}', { 0: conditionIndex + 1 });

/** All reminder actions across the dialog whose trigger_node_id points at the given timer node. */
export const findReminderActionsFor = (timerNodeId: string, nodes: Node[]): ReminderAction[] => {
  const all: ReminderAction[] = [];
  for (const node of nodes) {
    for (const action of collectActionsFromNode(node)) {
      if (isReminderAction(action) && action.trigger_node_id === timerNodeId) {
        all.push(action);
      }
    }
  }
  return all;
};

/** Number of reminder actions that point at the given timer node. */
export const countTimerReferences = (timerNodeId: string, nodes: Node[]): number =>
  findReminderActionsFor(timerNodeId, nodes).length;

export type TimerConditionLabel = {
  conditionId: string;
  conditionName: string | null;
  conditionIndex: number;
  reminderSeconds: number;
};

type ReminderActionLocation =
  | { kind: 'condition'; node: Node; condition: Condition; conditionIndex: number }
  | { kind: 'node'; node: Node };

const findReminderActionLocation = (
  actionId: string,
  nodes: Node[]
): ReminderActionLocation | null => {
  for (const node of nodes) {
    if ((node.actions ?? []).some((a) => a.action_id === actionId)) {
      return { kind: 'node', node };
    }
    const conditions = node.conditions ?? [];
    for (let i = 0; i < conditions.length; i += 1) {
      const condition = conditions[i];
      if ((condition.actions ?? []).some((a) => a.action_id === actionId)) {
        return { kind: 'condition', node, condition, conditionIndex: i };
      }
    }
  }
  return null;
};

/**
 * Trace a reminder action back to the originating condition (branch) in the dialog.
 * Walks up timer chains: if the reminder lives in a node's actions (not in a condition),
 * find the reminder that spawned that node and recurse, until we reach a condition or
 * exhaust the chain. Returns null if no originating condition is found.
 */
const traceReminderToBranch = (
  reminder: ReminderAction,
  nodes: Node[],
  visitedNodeIds: Set<string> = new Set()
): { condition: Condition; conditionIndex: number } | null => {
  const location = findReminderActionLocation(reminder.action_id, nodes);
  if (!location) {
    return null;
  }
  if (location.kind === 'condition') {
    return { condition: location.condition, conditionIndex: location.conditionIndex };
  }
  if (visitedNodeIds.has(location.node.node_id)) {
    return null;
  }
  visitedNodeIds.add(location.node.node_id);
  // Reminder lives in a node's actions: find the upstream reminder that spawned this node.
  const upstreamReminders = findReminderActionsFor(location.node.node_id, nodes);
  for (const upstream of upstreamReminders) {
    const branch = traceReminderToBranch(upstream, nodes, visitedNodeIds);
    if (branch) {
      return branch;
    }
  }
  return null;
};

/**
 * Branch labels for a timer node, walking up timer chains so chained timers still
 * show the originating branch. Each entry corresponds to one reminder action that
 * triggers this timer; reminderSeconds is THIS timer's wait, not the chain's total.
 */
export const getBranchLabelsForTimer = (
  timerNodeId: string,
  allNodes: Node[]
): TimerConditionLabel[] => {
  const reminders = findReminderActionsFor(timerNodeId, allNodes);
  const seenConditionIds = new Set<string>();
  const labels: TimerConditionLabel[] = [];
  for (const reminder of reminders) {
    const branch = traceReminderToBranch(reminder, allNodes);
    if (!branch) {
      continue;
    }
    if (seenConditionIds.has(branch.condition.condition_id)) {
      continue;
    }
    seenConditionIds.add(branch.condition.condition_id);
    labels.push({
      conditionId: branch.condition.condition_id,
      conditionName: branch.condition.name?.trim() || null,
      conditionIndex: branch.conditionIndex,
      reminderSeconds: reminder.reminder_seconds,
    });
  }
  return labels;
};

/**
 * Conditions on the parent node whose reminder action points at this timer.
 * Returns raw fields per condition; consumers format the branch label via i18n.
 * Use {@link getBranchLabelsForTimer} when you want to walk timer chains;
 * this version only looks at the immediate parent.
 */
export const getConditionLabelsForTimer = (
  timerNodeId: string,
  parentNode: Node | undefined
): TimerConditionLabel[] => {
  if (!parentNode?.conditions) {
    return [];
  }
  const labels: TimerConditionLabel[] = [];
  parentNode.conditions.forEach((condition: Condition, index: number) => {
    const reminderAction = (condition.actions ?? []).find(
      (a): a is ReminderAction => isReminderAction(a) && a.trigger_node_id === timerNodeId
    );
    if (reminderAction) {
      labels.push({
        conditionId: condition.condition_id,
        conditionName: condition.name?.trim() || null,
        conditionIndex: index,
        reminderSeconds: reminderAction.reminder_seconds,
      });
    }
  });
  return labels;
};
